import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown, ArrowUpDown, Search, Users, TrendingUp, Target, ChevronLeft, ChevronRight, ChevronDown, ListTree, List } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { Card } from '../../../components/ui'
import { formatPhone } from '../../../utils/masks'
import { inputClass } from '../../../styles/forms'

interface LeaderRanking {
  id: number
  name: string
  nickname: string | null
  phone: string
  parent_leader_id: number | null
  city_name: string | null
  state_code: string | null
  created_at: string
  total_people: number
  voters_count: number
  contacts_count: number
  areas_count: number
  vote_goal: number | null
  votes_obtained: number | null
}

type SortField = 'name' | 'total_people' | 'voters_count' | 'contacts_count' | 'vote_goal'
type ViewMode = 'grouped' | 'flat'

interface Totals { people: number; voters: number; contacts: number; groupSize: number }
interface TreeRow { leader: LeaderRanking; depth: number; hasChildren: boolean; totals: Totals }

export default function ReportsLeadersPage() {
  const [leaders, setLeaders] = useState<LeaderRanking[]>([])
  const [hasCampaign, setHasCampaign] = useState(false)
  const [loading, setLoading] = useState(true)
  const [sortField, setSortField] = useState<SortField>('total_people')
  const [sortAsc, setSortAsc] = useState(false)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grouped')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const navigate = useNavigate()

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    try {
      const res = await axios.get(buildApiUrl('/v1/reports/leaders/ranking'))
      setLeaders(res.data.data.leaders)
      setHasCampaign(res.data.data.has_campaign)
    } catch { /* */ } finally { setLoading(false) }
  }

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortAsc(!sortAsc)
    } else {
      setSortField(field)
      setSortAsc(field === 'name')
    }
  }

  // ---- Estrutura em árvore (para a visão agrupada) ----
  const { childrenMap, roots, totalsMap } = useMemo(() => {
    const byId = new Map(leaders.map(l => [l.id, l]))
    const childrenMap = new Map<number, LeaderRanking[]>()
    const roots: LeaderRanking[] = []
    for (const l of leaders) {
      const pid = l.parent_leader_id
      if (pid && byId.has(pid)) {
        const arr = childrenMap.get(pid) || []
        arr.push(l)
        childrenMap.set(pid, arr)
      } else {
        roots.push(l)
      }
    }
    // Totais do subárvore (o próprio + todos os subordinados)
    const totalsMap = new Map<number, Totals>()
    const compute = (l: LeaderRanking): Totals => {
      if (totalsMap.has(l.id)) return totalsMap.get(l.id)!
      let people = l.total_people, voters = l.voters_count, contacts = l.contacts_count, groupSize = 1
      for (const c of childrenMap.get(l.id) || []) {
        const t = compute(c)
        people += t.people; voters += t.voters; contacts += t.contacts; groupSize += t.groupSize
      }
      const t = { people, voters, contacts, groupSize }
      totalsMap.set(l.id, t)
      return t
    }
    leaders.forEach(compute)
    return { childrenMap, roots, totalsMap }
  }, [leaders])

  function sortValue(l: LeaderRanking) {
    if (sortField === 'name') return l.name.toLowerCase()
    const t = totalsMap.get(l.id)
    if (sortField === 'voters_count') return t?.voters ?? 0
    if (sortField === 'contacts_count') return t?.contacts ?? 0
    if (sortField === 'vote_goal') return l.vote_goal ?? 0
    return t?.people ?? 0
  }
  function sortNodes(list: LeaderRanking[]) {
    return [...list].sort((a, b) => {
      const va = sortValue(a), vb = sortValue(b)
      if (va < vb) return sortAsc ? -1 : 1
      if (va > vb) return sortAsc ? 1 : -1
      return 0
    })
  }

  // Linhas visíveis da árvore (respeita busca + expandir/recolher)
  const treeRows = useMemo<TreeRow[]>(() => {
    const q = search.toLowerCase()
    const matches = (l: LeaderRanking) =>
      !q || l.name.toLowerCase().includes(q) || (l.nickname?.toLowerCase().includes(q) ?? false) || (l.city_name?.toLowerCase().includes(q) ?? false)
    const subtreeMatches = (l: LeaderRanking): boolean =>
      matches(l) || (childrenMap.get(l.id) || []).some(subtreeMatches)

    const rows: TreeRow[] = []
    const walk = (l: LeaderRanking, depth: number) => {
      const kids = (childrenMap.get(l.id) || []).filter(subtreeMatches)
      const hasChildren = kids.length > 0
      rows.push({ leader: l, depth, hasChildren, totals: totalsMap.get(l.id)! })
      const isOpen = expanded.has(l.id) || !!q // ao buscar, abre automaticamente
      if (hasChildren && isOpen) sortNodes(kids).forEach(k => walk(k, depth + 1))
    }
    sortNodes(roots.filter(subtreeMatches)).forEach(r => walk(r, 0))
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, childrenMap, totalsMap, expanded, search, sortField, sortAsc])

  // Lista plana (visão separada) — igual ao comportamento anterior
  const filtered = useMemo(() => {
    let list = [...leaders]
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(l => l.name.toLowerCase().includes(q) || l.nickname?.toLowerCase().includes(q) || l.city_name?.toLowerCase().includes(q))
    }
    list.sort((a, b) => {
      let va: any = a[sortField] ?? 0
      let vb: any = b[sortField] ?? 0
      if (sortField === 'name') { va = va.toLowerCase(); vb = vb.toLowerCase() }
      if (va < vb) return sortAsc ? -1 : 1
      if (va > vb) return sortAsc ? 1 : -1
      return 0
    })
    return list
  }, [leaders, sortField, sortAsc, search])

  function toggleExpand(id: number) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function expandAll() { setExpanded(new Set(leaders.filter(l => (childrenMap.get(l.id) || []).length > 0).map(l => l.id))) }
  function collapseAll() { setExpanded(new Set()) }

  // Totais
  const totalPeople = leaders.reduce((s, l) => s + l.total_people, 0)
  const totalVoters = leaders.reduce((s, l) => s + l.voters_count, 0)
  const avgPerLeader = leaders.length ? Math.round(totalPeople / leaders.length) : 0

  const anyExpandable = leaders.some(l => (childrenMap.get(l.id) || []).length > 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/reports/overview')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ranking de Lideranças</h1>
          <p className="text-gray-500 mt-1">Desempenho e captação por liderança</p>
        </div>
      </div>

      {/* Stats resumo */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: <Crown className="w-5 h-5 text-brand-500" />, value: leaders.length, label: 'Lideranças ativas' },
            { icon: <Users className="w-5 h-5 text-emerald-500" />, value: totalPeople, label: 'Pessoas captadas' },
            { icon: <TrendingUp className="w-5 h-5 text-blue-500" />, value: avgPerLeader, label: 'Media por lider' },
            { icon: <Target className="w-5 h-5 text-amber-500" />, value: totalVoters, label: 'Eleitores' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">{s.icon}</div>
              </div>
              <p className="text-2xl font-semibold text-gray-900">{s.value.toLocaleString('pt-BR')}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Ranking de Lideranças */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Ranking de Lideranças</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {viewMode === 'grouped' ? 'Agrupado por liderança pai — clique para abrir os subordinados' : 'Ordenar por coluna para analisar desempenho'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Toggle Agrupado / Separado */}
              <div className="inline-flex rounded-xl border border-gray-200 p-0.5 bg-gray-50">
                <button
                  onClick={() => setViewMode('grouped')}
                  className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    viewMode === 'grouped' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
                >
                  <ListTree className="w-3.5 h-3.5" /> Agrupado
                </button>
                <button
                  onClick={() => setViewMode('flat')}
                  className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    viewMode === 'flat' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-700')}
                >
                  <List className="w-3.5 h-3.5" /> Separado
                </button>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar liderança..." className={clsx(inputClass, 'pl-9')} />
              </div>
            </div>
          </div>
          {viewMode === 'grouped' && anyExpandable && !search && (
            <div className="flex items-center gap-2 mt-3">
              <button onClick={expandAll} className="text-xs text-brand-600 hover:text-brand-700 font-medium">Expandir tudo</button>
              <span className="text-gray-300">·</span>
              <button onClick={collapseAll} className="text-xs text-gray-500 hover:text-gray-700 font-medium">Recolher tudo</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}
          </div>
        ) : (viewMode === 'grouped' ? treeRows.length === 0 : filtered.length === 0) ? (
          <div className="py-16 text-center">
            <Crown className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">{search ? 'Nenhuma liderança encontrada' : 'Nenhuma liderança ativa'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                  <SortHeader label="Liderança" field="name" current={sortField} asc={sortAsc} onSort={toggleSort} />
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cidade</th>
                  <SortHeader label="Pessoas" field="total_people" current={sortField} asc={sortAsc} onSort={toggleSort} align="right" />
                  <SortHeader label="Eleitores" field="voters_count" current={sortField} asc={sortAsc} onSort={toggleSort} align="right" />
                  <SortHeader label="Contatos" field="contacts_count" current={sortField} asc={sortAsc} onSort={toggleSort} align="right" />
                  {hasCampaign && <SortHeader label="Meta" field="vote_goal" current={sortField} asc={sortAsc} onSort={toggleSort} align="right" />}
                </tr>
              </thead>
              <tbody>
                {viewMode === 'grouped'
                  ? treeRows.map((row, i) => (
                    <GroupedRow key={row.leader.id} row={row} index={i} hasCampaign={hasCampaign}
                      isOpen={expanded.has(row.leader.id) || !!search}
                      onToggle={() => toggleExpand(row.leader.id)} />
                  ))
                  : filtered.map((l, i) => {
                    const goalPct = l.vote_goal && l.vote_goal > 0 ? Math.round((l.total_people / l.vote_goal) * 100) : null
                    return (
                      <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3 text-xs font-bold text-gray-400">{i + 1}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-brand-600">{l.name.charAt(0)}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{l.name}</p>
                              {l.phone && <p className="text-xs text-gray-400">{formatPhone(l.phone)}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {l.city_name ? (
                            <span className="text-xs text-gray-500">{l.city_name}/{l.state_code}</span>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="text-sm font-semibold text-gray-900">{l.total_people.toLocaleString('pt-BR')}</span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="text-sm text-gray-700">{l.voters_count.toLocaleString('pt-BR')}</span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="text-sm text-gray-700">{l.contacts_count.toLocaleString('pt-BR')}</span>
                        </td>
                        {hasCampaign && (
                          <td className="px-3 py-3 text-right">
                            {l.vote_goal != null ? (
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{
                                    width: `${Math.min(goalPct || 0, 100)}%`,
                                    backgroundColor: (goalPct || 0) >= 100 ? '#22c55e' : (goalPct || 0) >= 50 ? '#f59e0b' : '#ef4444',
                                  }} />
                                </div>
                                <span className={clsx('text-xs font-medium', (goalPct || 0) >= 100 ? 'text-success-600' : (goalPct || 0) >= 50 ? 'text-warning-600' : 'text-error-500')}>
                                  {goalPct}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })
                }
              </tbody>
              <tfoot>
                <tr className="bg-gray-50/80">
                  <td className="px-5 py-3" colSpan={3}>
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      {viewMode === 'grouped'
                        ? `Total (${roots.length} grupos · ${leaders.length} lideranças)`
                        : `Total (${filtered.length} lideranças)`}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">{totalPeople.toLocaleString('pt-BR')}</span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-bold text-gray-700">{totalVoters.toLocaleString('pt-BR')}</span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-bold text-gray-700">{leaders.reduce((s, l) => s + l.contacts_count, 0).toLocaleString('pt-BR')}</span>
                  </td>
                  {hasCampaign && <td className="px-3 py-3" />}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function GroupedRow({ row, index, hasCampaign, isOpen, onToggle }: {
  row: TreeRow; index: number; hasCampaign: boolean; isOpen: boolean; onToggle: () => void
}) {
  const { leader: l, depth, hasChildren, totals } = row
  const goalPct = l.vote_goal && l.vote_goal > 0 ? Math.round((totals.people / l.vote_goal) * 100) : null
  return (
    <tr
      className={clsx('border-b border-gray-50 transition-colors', hasChildren ? 'cursor-pointer hover:bg-brand-50/40' : 'hover:bg-gray-50/50', depth > 0 && 'bg-gray-50/30')}
      onClick={hasChildren ? onToggle : undefined}
    >
      <td className="px-5 py-3 text-xs font-bold text-gray-400">{depth === 0 ? index + 1 : ''}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2" style={{ paddingLeft: depth * 20 }}>
          {hasChildren ? (
            <button onClick={(e) => { e.stopPropagation(); onToggle() }} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 flex-shrink-0">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-5 flex-shrink-0" />
          )}
          <div className={clsx('rounded-full flex items-center justify-center flex-shrink-0', depth === 0 ? 'w-8 h-8 bg-brand-50' : 'w-7 h-7 bg-gray-100')}>
            <span className={clsx('font-bold', depth === 0 ? 'text-xs text-brand-600' : 'text-[11px] text-gray-500')}>{l.name.charAt(0)}</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className={clsx('truncate', depth === 0 ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-700')}>{l.name}</p>
              {hasChildren && (
                <span className="text-[10px] font-semibold text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {totals.groupSize} no grupo
                </span>
              )}
            </div>
            {l.phone && <p className="text-xs text-gray-400">{formatPhone(l.phone)}</p>}
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        {l.city_name ? <span className="text-xs text-gray-500">{l.city_name}/{l.state_code}</span> : <span className="text-xs text-gray-300">-</span>}
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm font-semibold text-gray-900">{totals.people.toLocaleString('pt-BR')}</span>
        {hasChildren && totals.people !== l.total_people && (
          <p className="text-[10px] text-gray-400">próprios: {l.total_people.toLocaleString('pt-BR')}</p>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{totals.voters.toLocaleString('pt-BR')}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{totals.contacts.toLocaleString('pt-BR')}</span>
      </td>
      {hasCampaign && (
        <td className="px-3 py-3 text-right">
          {l.vote_goal != null ? (
            <div className="flex items-center justify-end gap-2">
              <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{
                  width: `${Math.min(goalPct || 0, 100)}%`,
                  backgroundColor: (goalPct || 0) >= 100 ? '#22c55e' : (goalPct || 0) >= 50 ? '#f59e0b' : '#ef4444',
                }} />
              </div>
              <span className={clsx('text-xs font-medium', (goalPct || 0) >= 100 ? 'text-success-600' : (goalPct || 0) >= 50 ? 'text-warning-600' : 'text-error-500')}>
                {goalPct}%
              </span>
            </div>
          ) : (
            <span className="text-xs text-gray-300">-</span>
          )}
        </td>
      )}
    </tr>
  )
}

function SortHeader({ label, field, current, asc, onSort, align }: {
  label: string; field: SortField; current: SortField; asc: boolean; onSort: (f: SortField) => void; align?: 'right'
}) {
  const active = current === field
  return (
    <th className={clsx('px-3 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:text-brand-600 transition-colors', align === 'right' ? 'text-right' : 'text-left', active ? 'text-brand-600' : 'text-gray-500')}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={clsx('w-3 h-3', active ? 'text-brand-500' : 'text-gray-300')} />
      </span>
    </th>
  )
}
